<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useFirebaseAuth } from "vuefire";
import { useArticleStore } from '@/stores/article';
import { ArticleEndpoint } from "@/enums.ts";
import { LogCode, useComponentLogger } from "@/logger";
import { getNlaUsers } from "@/stores/utils.ts";
import { usePropValidator } from "@/composables/usePropValidator.ts";
import { createCorrelationId } from '@/lib/correlationId';

// Cannot import FetchArticleParams type due to https://github.com/vuejs/core/issues/8301
const props = defineProps<{
  ids: string,
  endpoint: ArticleEndpoint,
  branding?: string,
  mmoId?: string,
  P1?: string,
  P2?: string,
  P3?: string,
  P4?: string,
  P5?: string,
}>();


const articleStore = useArticleStore();
const logger = useComponentLogger();
const _user = useFirebaseAuth()!.currentUser;
const route = useRoute();
const correlationId = typeof route.query.correlationId === "string"
  ? route.query.correlationId
  : createCorrelationId();

const {
  hasMandatoryParams,
  validationErrorMessage
} = usePropValidator(props, ['ids']);

onMounted(async () => {
  if (hasMandatoryParams.value) {
    await articleStore.fetchArticle(props, correlationId);
  } else {
    const nlaUsers = await getNlaUsers(_user!);
    const nlaUserIds = nlaUsers?.map(user => user.id) ?? [];
    logger.warn('View Article: ' + validationErrorMessage.value, {
      logCode: LogCode.VIEW_ARTICLE_MISSING_ID_PARAM,
      dataFields: {
        userIds: nlaUserIds,
        articleIds: props.ids || '[MISSING]',
        mmoId: props.mmoId,
        branding: props.branding,
      },
      url: props.endpoint,
      correlationId,
    });
  }
});

const errorMessage = computed(() => {
  if (validationErrorMessage.value) {
    return `${validationErrorMessage.value}\n\nError code: ${LogCode.VIEW_ARTICLE_MISSING_ID_PARAM}\nReference ID: ${correlationId}`;
  }
  if (articleStore.error && articleStore.isFinished) {
    return articleStore.error;
  }
  return null;
});


</script>

<template>
  <div class="card mx-auto">
    <div class="card-body">
      <div class="card-text">
        <div v-if="errorMessage" class="error-message">
          {{ errorMessage }}
        </div>

        <div v-else-if="articleStore.isFinished" class="alert alert-info">
          <p>If you're seeing this and the link has not opened, then your browser may have blocked it.</p>
          <p>Please check your browser settings to allow popups for this site</p>
        </div>

        <div v-else>
          <span class="loader" />
          <p>Generating PDF... This may take a few moments...</p>
        </div>
      </div>
    </div>
  </div>
</template>
